/**
 * BeeFree "Make call" Icons block bug:
 *   <a href="tel">{{job_owner_mobile}}</a>
 *
 * The label holds the value (merge tag or literal number) but `href` is bare "tel" (no ":"),
 * so the rendered email has no tap-to-call link. We want:
 *   <a href="tel:{{job_owner_mobile}}">{{job_owner_mobile}}</a>
 * so the backend merge-tag replacement produces e.g. `tel:0410009191` on send.
 *
 * We fix both JSON (so re-opening in BEE keeps the fix) and HTML (what is sent).
 * Nothing else in the email is touched.
 */

const MERGE_ONLY = /^\{\{[\w-]+\}\}$/;
const ICONS_MODULE_TYPE = "mailup-bee-newsletter-modules-icons";

const isBareTel = href => {
  const h = (href || "").trim().toLowerCase();
  return h === "" || h === "tel" || h === "http://tel" || h === "https://tel" || h === "http://tel/" || h === "https://tel/";
};

/**
 * Extract a value for `tel:<value>`: prefer the anchor text, else any sibling cell text.
 * Strips a leading "tel:" if present.
 */
const valueForTel = anchor => {
  let text = (anchor.textContent || "").replace(/\s+/g, " ").trim();
  if (!text) {
    const tr = anchor.closest("tr");
    if (tr) {
      tr.querySelectorAll("td").forEach(td => {
        if (td.contains(anchor)) return;
        const t = (td.textContent || "").replace(/\s+/g, " ").trim();
        if (t) text = t;
      });
    }
  }
  if (text.toLowerCase().startsWith("tel:")) text = text.slice(4).trim();
  return text;
};

/**
 * @param {string|undefined} html
 * @returns {string|undefined}
 */
export function fixBeeIconTelHtml(html) {
  if (typeof html !== "string" || !html) return html;
  if (!html.includes('href="tel"') && !html.includes("href='tel'")) return html;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    if (!doc.body) return html;

    let modified = false;
    doc.querySelectorAll('a[href="tel"]').forEach(a => {
      const value = valueForTel(a);
      if (!value) return;
      a.setAttribute("href", `tel:${value}`);
      modified = true;
    });

    if (!modified) return html;

    const hasDoctype = /<!DOCTYPE/i.test(html);
    const hasHtmlTag = /<html[\s>]/i.test(html);
    if ((hasDoctype || hasHtmlTag) && doc.documentElement) {
      const out = doc.documentElement.outerHTML;
      return hasDoctype ? `<!DOCTYPE html>\n${out}` : out;
    }
    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

/**
 * @param {string|object|undefined} json
 * @returns {string|object|undefined}
 */
export function fixBeeIconTelJson(json) {
  if (json == null) return json;
  const wasString = typeof json === "string";
  let root;
  try {
    root = wasString ? JSON.parse(json) : JSON.parse(JSON.stringify(json));
  } catch {
    return json;
  }

  let changed = false;
  const walk = rows => {
    if (!Array.isArray(rows)) return;
    rows.forEach(row =>
      (row.columns || []).forEach(col =>
        (col.modules || []).forEach(mod => {
          if (!mod || mod.type !== ICONS_MODULE_TYPE) return;
          const icons = mod.descriptor && mod.descriptor.iconsList && mod.descriptor.iconsList.icons;
          if (!Array.isArray(icons)) return;
          icons.forEach(icon => {
            if (!icon) return;
            const label = String(icon.text || "").trim();
            if (!label) return;
            const value = label.toLowerCase().startsWith("tel:") ? label.slice(4).trim() : label;
            const href = String(icon.href || "").trim();
            if (MERGE_ONLY.test(href)) {
              icon.href = `tel:${href}`;
              changed = true;
            } else if (isBareTel(href)) {
              icon.href = `tel:${value}`;
              changed = true;
            }
          });
        })
      )
    );
  };

  if (root.page) walk(root.page.rows);
  if (root.template && root.template.page) walk(root.template.page.rows);

  if (!changed) return json;
  return wasString ? JSON.stringify(root) : root;
}

/**
 * @param {string|object|undefined} jsonTemplate
 * @param {string|undefined} htmlTemplate
 */
export function fixBeeIconTelLinks(jsonTemplate, htmlTemplate) {
  return {
    jsonTemplate: fixBeeIconTelJson(jsonTemplate),
    htmlTemplate: fixBeeIconTelHtml(htmlTemplate)
  };
}
